import { useEffect, useRef, useState } from "react";
import { api, API, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Pencil, Mail, Phone, HardHat, Upload, Send, FileText, CheckCircle2, Copy, ClipboardList, ThumbsUp, MessageSquareWarning, XCircle, ShieldCheck, AlertTriangle, KeyRound, Ban, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLES = {
  draft: "bg-slate-100 text-slate-600",
  agreement_sent: "bg-amber-100 text-amber-700",
  agreement_signed: "bg-emerald-100 text-emerald-700",
  profile_completed: "bg-blue-100 text-blue-700",
  under_review: "bg-indigo-100 text-indigo-700",
  changes_requested: "bg-orange-100 text-orange-700",
  rejected: "bg-red-100 text-red-700",
  active: "bg-green-600 text-white",
};

const emptyForm = {
  name: "", email: "", mobile: "", position: "", start_date: "", supervisor: "",
  rates: { metre_rate: "", standard_rate: "", saturday_rate: "", overtime_rate: "", travel_rate: "" },
  rate_notes: "",
};

const RATE_FIELDS = [
  ["metre_rate", "Metre Rate ($/m)"],
  ["standard_rate", "Standard Hourly (Mon–Fri)"],
  ["saturday_rate", "Saturday Hourly"],
  ["overtime_rate", "Approved Overtime"],
  ["travel_rate", "Approved Travel"],
];

function RotatingContractorCard() {
  const [st, setSt] = useState(null);
  const [editing, setEditing] = useState(false);
  const [showHist, setShowHist] = useState(false);
  const [f, setF] = useState({});
  const load = () => api.get("/rotating-contractor").then((r) => setSt(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!st) return null;
  const cur = st.current;
  const rr = (cur && cur.rates) || {};
  const money = (v) => (v || v === 0 ? `$${v}` : "—");
  const openEdit = (fresh) => {
    if (fresh || !cur) setF({ rates: {}, login_email: st.login.email || "" });
    else setF({ name: cur.name, email: cur.email, mobile: cur.mobile, rates: { ...rr }, start_date: cur.start_date, end_date: cur.end_date, notes: cur.notes });
    setEditing(true);
  };
  const save = async () => {
    if (!f.name) { toast.error("Enter the contractor's name."); return; }
    try { await api.post("/rotating-contractor/save", f); toast.success("Rotating Contractor saved."); setEditing(false); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const resetPw = async () => {
    const pw = window.prompt("Set a new password for the Rotating Contractor login" + (st.login.email ? ` (${st.login.email})` : ""));
    if (!pw) return;
    const login_email = st.login.email || window.prompt("Set the login email (e.g. rotating@truesealcaulking.com.au)") || "";
    try { await api.post("/rotating-contractor/password", { password: pw, login_email }); toast.success("Login password updated."); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const deactivate = async () => {
    if (!window.confirm("Deactivate the current rotating contractor? Their history is kept and they stop appearing in job assignment.")) return;
    try { await api.post("/rotating-contractor/deactivate", {}); toast.success("Deactivated."); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const rateField = (k, label) => (
    <div><Label className="text-xs">{label}</Label><Input type="number" step="0.01" value={f.rates?.[k] ?? ""} onChange={(e) => setF((p) => ({ ...p, rates: { ...p.rates, [k]: e.target.value === "" ? null : parseFloat(e.target.value) } }))} className="mt-1" /></div>
  );
  return (
    <div data-testid="rotating-contractor-card" className="bg-gradient-to-br from-navy to-[#12233b] text-white rounded-2xl p-6 shadow-sm">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[11px] uppercase tracking-wider text-slate-300 font-semibold flex items-center gap-1.5"><RefreshCw className="w-3.5 h-3.5" />Rotating Contractor</p>
          {cur ? (
            <>
              <p className="text-2xl font-heading font-bold mt-1" data-testid="rotating-current-name">{cur.name}</p>
              <p className="text-sm text-slate-300 mt-0.5">{cur.email || "no email"} · {cur.mobile || "no mobile"}</p>
              <p className="text-xs text-slate-300 mt-1" data-testid="rotating-emails-go-to">Emails go to: <span className="text-white font-medium">{cur.email || "no email set (add one to receive timesheets)"}</span></p>
              {(cur.start_date || cur.end_date) && <p className="text-xs text-slate-300 mt-0.5" data-testid="rotating-active-period">Shows on calendar: {cur.start_date || "any"} – {cur.end_date || "ongoing"}</p>}
              <div className="flex items-center gap-3 mt-2 flex-wrap text-sm">
                <span className="bg-white/15 rounded-full px-3 py-1" data-testid="rotating-current-rate">Rate {money(rr.standard_rate)}/hr</span>
                <span className="bg-emerald-500/30 text-emerald-100 rounded-full px-3 py-1">Active</span>
                <span className="text-slate-300 text-xs">Login: {st.login.email || "not set"} {st.login.has_password ? "· password set" : "· no password"}</span>
              </div>
            </>
          ) : (
            <p className="text-slate-300 mt-1 text-sm">No current rotating contractor. Set one up to assign casual/short-term workers.</p>
          )}
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mt-4">
        <Button size="sm" data-testid="rotating-edit-btn" onClick={() => openEdit(false)} className="bg-white text-navy hover:bg-slate-100">{cur ? "Edit Current Contractor" : "Set Up Rotating Contractor"}</Button>
        {cur && <Button size="sm" variant="outline" data-testid="rotating-switch-btn" onClick={() => openEdit(true)} className="bg-transparent border-white/30 text-white hover:bg-white/10">Switch to New Person</Button>}
        <Button size="sm" variant="outline" data-testid="rotating-password-btn" onClick={resetPw} className="bg-transparent border-white/30 text-white hover:bg-white/10">Reset Password</Button>
        {cur && <Button size="sm" variant="outline" data-testid="rotating-deactivate-btn" onClick={deactivate} className="bg-transparent border-white/30 text-white hover:bg-white/10">Deactivate</Button>}
        {st.history.length > 0 && <Button size="sm" variant="outline" data-testid="rotating-history-btn" onClick={() => setShowHist((s) => !s)} className="bg-transparent border-white/30 text-white hover:bg-white/10">View Previous ({st.history.length})</Button>}
      </div>
      {showHist && (
        <div className="mt-4 bg-white/10 rounded-xl p-3 space-y-2" data-testid="rotating-history">
          {st.history.map((h) => (
            <div key={h.id} className="flex items-center justify-between text-sm border-b border-white/10 pb-2 last:border-0">
              <span>{h.name} <span className="text-slate-300">· {h.start_date || "?"} – {h.end_date || "?"}</span></span>
              <span className="text-slate-200">${(h.rates || {}).standard_rate ?? "—"}/hr</span>
            </div>
          ))}
        </div>
      )}
      {editing && (
        <div className="mt-4 bg-white text-slate-800 rounded-xl p-4 space-y-3" data-testid="rotating-edit-form">
          <p className="text-xs text-slate-500">{cur && f.email && f.email.toLowerCase() === (cur.email || "").toLowerCase() ? "Editing the current person's details." : "A new name/email will switch the seat to a new person (the current one is archived, history kept)."}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label className="text-xs">Name</Label><Input data-testid="rotating-f-name" value={f.name || ""} onChange={(e) => setF((p) => ({ ...p, name: e.target.value }))} className="mt-1" /></div>
            <div><Label className="text-xs">Email</Label><Input data-testid="rotating-f-email" value={f.email || ""} onChange={(e) => setF((p) => ({ ...p, email: e.target.value }))} className="mt-1" /><p className="text-[11px] text-slate-500 mt-1" data-testid="rotating-f-email-note">Timesheets, job alerts &amp; reminders are sent here.</p></div>
            <div><Label className="text-xs">Mobile</Label><Input value={f.mobile || ""} onChange={(e) => setF((p) => ({ ...p, mobile: e.target.value }))} className="mt-1" /></div>
            {rateField("standard_rate", "Hourly rate")}
            {rateField("saturday_rate", "Saturday rate")}
            {rateField("overtime_rate", "Overtime rate")}
            {rateField("travel_rate", "Travel rate")}
            {rateField("metre_rate", "Metre rate")}
            <div><Label className="text-xs">Start date</Label><Input type="date" value={f.start_date || ""} onChange={(e) => setF((p) => ({ ...p, start_date: e.target.value }))} className="mt-1" /></div>
            <div><Label className="text-xs">End / inactive date</Label><Input type="date" value={f.end_date || ""} onChange={(e) => setF((p) => ({ ...p, end_date: e.target.value }))} className="mt-1" /></div>
            <p className="sm:col-span-2 text-[11px] text-slate-500 -mt-1" data-testid="rotating-dates-note">They only appear on the calendar between these dates (use the same date for a one-day booking).</p>
          </div>
          <div><Label className="text-xs">Notes (optional)</Label><Input value={f.notes || ""} onChange={(e) => setF((p) => ({ ...p, notes: e.target.value }))} className="mt-1" /></div>
          {!st.login.email && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 border-t pt-3">
              <div><Label className="text-xs">Login email (set once)</Label><Input data-testid="rotating-f-login-email" value={f.login_email || ""} onChange={(e) => setF((p) => ({ ...p, login_email: e.target.value }))} placeholder="rotating@truesealcaulking.com.au" className="mt-1" /><p className="text-[11px] text-slate-500 mt-1" data-testid="rotating-f-login-note">Shared sign-in only. No emails are sent to this address.</p></div>
              <div><Label className="text-xs">Login password</Label><Input data-testid="rotating-f-password" type="text" value={f.password || ""} onChange={(e) => setF((p) => ({ ...p, password: e.target.value }))} className="mt-1" /></div>
            </div>
          )}
          <div className="flex gap-2 justify-end">
            <Button size="sm" variant="outline" onClick={() => setEditing(false)}>Cancel</Button>
            <Button size="sm" data-testid="rotating-save-btn" onClick={save} className="bg-navy text-white hover:bg-navy/90">Save &amp; Activate</Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Contractors({ workerType = "contractor" }) {
  const isEmp = workerType === "employee";
  const noun = isEmp ? "Employee" : "Contractor";
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [compliance, setCompliance] = useState(null);

  const load = () => {
    api.get("/contractors", { params: { worker_type: workerType } }).then((r) => setItems(r.data));
    api.get("/contractors/compliance").then((r) => setCompliance(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [workerType]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setRate = (k, v) => setForm((f) => ({ ...f, rates: { ...f.rates, [k]: v } }));

  const openNew = () => { setForm(emptyForm); setEditId(null); setOpen(true); };
  const openEdit = (c) => {
    setForm({
      name: c.name || "", email: c.email || "", mobile: c.mobile || "", position: c.position || "",
      start_date: c.start_date || "", supervisor: c.supervisor || "",
      rates: { ...emptyForm.rates, ...(c.rates || {}) },
      rate_notes: c.rate_notes || "",
    });
    setEditId(c.id); setOpen(true);
  };

  const save = async () => {
    try {
      if (editId) await api.put(`/contractors/${editId}`, form);
      else await api.post("/contractors", { ...form, worker_type: workerType });
      toast.success(`${noun} saved`); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <p className="text-slate-500">{items.length} {noun.toLowerCase()}{items.length !== 1 ? "s" : ""}</p>
        <Button data-testid="new-contractor-button" onClick={openNew} className="bg-navy hover:bg-navy-light"><Plus className="w-4 h-4 mr-2" />Add {noun}</Button>
      </div>

      {compliance && (compliance.expired > 0 || compliance.expiring > 0) && (
        <div data-testid="compliance-banner" className={`rounded-2xl p-4 border flex items-start gap-3 ${compliance.expired > 0 ? "bg-red-50 border-red-200" : "bg-amber-50 border-amber-200"}`}>
          <AlertTriangle className={`w-5 h-5 shrink-0 ${compliance.expired > 0 ? "text-red-600" : "text-amber-600"}`} />
          <div className="text-sm">
            <p className={`font-heading font-semibold ${compliance.expired > 0 ? "text-red-800" : "text-amber-800"}`}>Compliance attention needed</p>
            <p className={compliance.expired > 0 ? "text-red-700" : "text-amber-700"}>
              {compliance.expired > 0 && <span data-testid="compliance-expired-count"><b>{compliance.expired}</b> document{compliance.expired !== 1 ? "s" : ""} expired</span>}
              {compliance.expired > 0 && compliance.expiring > 0 && " · "}
              {compliance.expiring > 0 && <span data-testid="compliance-expiring-count"><b>{compliance.expiring}</b> expiring within 30 days</span>}
            </p>
            <div className="mt-1.5 space-y-0.5">
              {compliance.items.slice(0, 5).map((it, i) => (
                <p key={i} className="text-xs text-slate-600">{it.name} — {it.label}: {it.status === "expired" ? `expired ${Math.abs(it.days)} day(s) ago` : `expires in ${it.days} day(s)`} ({it.expiry})</p>
              ))}
            </div>
          </div>
        </div>
      )}

      {!isEmp && <RotatingContractorCard />}

      {items.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-slate-200 p-12 text-center text-slate-400">
          <HardHat className="w-10 h-10 mx-auto mb-3 text-slate-300" />
          No {noun.toLowerCase()}s yet. Add one to begin onboarding.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((c) => (
            <div key={c.id} data-testid={`contractor-card-${c.id}`} className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
              <div className="flex justify-between items-start">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-full flex items-center justify-center bg-orange-100 text-orange-600"><HardHat className="w-5 h-5" /></div>
                  <div><p className="font-heading font-semibold text-navy">{c.name}</p><p className="text-xs text-slate-500">{c.position || noun}</p></div>
                </div>
                <span data-testid={`contractor-status-${c.id}`} className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${STATUS_STYLES[c.status] || "bg-slate-100 text-slate-600"}`}>{c.status_display}</span>
              </div>
              <div className="mt-4 space-y-1.5 text-sm text-slate-600">
                <p className="flex items-center gap-2 truncate"><Mail className="w-4 h-4 text-slate-400" />{c.email}</p>
                {c.mobile && <p className="flex items-center gap-2"><Phone className="w-4 h-4 text-slate-400" />{c.mobile}</p>}
              </div>
              {c.compliance?.status === "expired" && (
                <p data-testid={`compliance-badge-${c.id}`} className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-red-700 bg-red-50 border border-red-200 rounded-full px-2.5 py-1"><AlertTriangle className="w-3.5 h-3.5" />Insurance expired</p>
              )}
              {c.compliance?.status === "expiring" && (
                <p data-testid={`compliance-badge-${c.id}`} className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1"><AlertTriangle className="w-3.5 h-3.5" />Insurance expiring soon</p>
              )}
              {c.status === "active" && c.documents_complete === false && (
                <p data-testid={`docs-outstanding-badge-${c.id}`} className="mt-3 ml-2 inline-flex items-center gap-1.5 text-xs font-semibold text-amber-800 bg-amber-100 border border-amber-300 rounded-full px-2.5 py-1"><AlertTriangle className="w-3.5 h-3.5" />Documents outstanding · pay on hold</p>
              )}
              <div className="mt-4 flex gap-2">
                <Button data-testid={`manage-contractor-${c.id}`} onClick={() => setDetail(c)} variant="outline" size="sm" className="flex-1"><ClipboardList className="w-4 h-4 mr-1.5" />Manage</Button>
                <Button data-testid={`edit-contractor-${c.id}`} onClick={() => openEdit(c)} variant="outline" size="sm"><Pencil className="w-4 h-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{editId ? `Edit ${noun}` : `Add ${noun}`}</DialogTitle></DialogHeader>
          <div className="space-y-5">
            <section className="space-y-3">
              <h4 className="text-xs uppercase tracking-widest font-semibold text-slate-400">Personal Details</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label>Full Name *</Label><Input data-testid="contractor-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1" /></div>
                <div><Label>Email *</Label><Input data-testid="contractor-email-input" type="email" value={form.email} disabled={!!editId} onChange={(e) => set("email", e.target.value)} className="mt-1" /></div>
                <div><Label>Mobile</Label><Input value={form.mobile} onChange={(e) => set("mobile", e.target.value)} className="mt-1" /></div>
                <div><Label>Position / Trade</Label><Input value={form.position} onChange={(e) => set("position", e.target.value)} className="mt-1" /></div>
                <div><Label>Start Date</Label><Input type="date" value={form.start_date} onChange={(e) => set("start_date", e.target.value)} className="mt-1" /></div>
                <div><Label>Supervisor</Label><Input value={form.supervisor} onChange={(e) => set("supervisor", e.target.value)} className="mt-1" /></div>
              </div>
            </section>

            <section className="space-y-3">
              <h4 className="text-xs uppercase tracking-widest font-semibold text-slate-400">Agreed Rates Not Including Super</h4>
              <p className="text-xs text-slate-400 -mt-1">Super will be paid on top of these rates directly.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {RATE_FIELDS.map(([k, label]) => (
                  <div key={k}><Label>{label}</Label>
                    <Input data-testid={`contractor-rate-${k}`} type="number" step="0.01" value={form.rates[k] ?? ""} onChange={(e) => setRate(k, e.target.value === "" ? "" : parseFloat(e.target.value))} className="mt-1" /></div>
                ))}
              </div>
              <div><Label>Rate Notes</Label><Textarea value={form.rate_notes} onChange={(e) => set("rate_notes", e.target.value)} rows={2} className="mt-1" /></div>
            </section>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="contractor-save-button" onClick={save} className="bg-navy hover:bg-navy-light" disabled={!form.name || !form.email}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {detail && <ContractorDetail id={detail.id} workerType={workerType} onClose={() => setDetail(null)} onChange={load} />}
    </div>
  );
}

function ContractorDetail({ id, workerType = "contractor", onClose, onChange }) {
  const isEmp = workerType === "employee";
  const noun = isEmp ? "Employee" : "Contractor";
  const contractEndpoint = isEmp ? "/settings/employee-contract" : "/settings/onboarding-contract";
  const [c, setC] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const [defaultContract, setDefaultContract] = useState(null);

  const refresh = () => api.get(`/contractors/${id}`).then((r) => setC(r.data));
  useEffect(() => { refresh(); api.get(contractEndpoint).then((r) => setDefaultContract(r.data)).catch(() => {}); }, [id]);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      await api.post(`/contractors/${id}/agreement`, fd);
      toast.success("Agreement uploaded"); refresh(); onChange();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const sendAgreement = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/contractors/${id}/send-agreement`);
      toast.success(r.data.message || "Agreement sent"); refresh(); onChange();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const copyLink = () => {
    const link = `${window.location.origin}/onboard/${c.ack_token}`;
    navigator.clipboard.writeText(link); toast.success("Acknowledgement link copied");
  };

  const copyEmail = () => { navigator.clipboard.writeText(c.email); toast.success("Login email copied"); };

  const resendWelcome = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/contractors/${id}/resend-welcome`);
      toast.success(r.data.message || "Sign-in email resent"); refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const resetPassword = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/contractors/${id}/reset-password`);
      toast.success(r.data.message || "Password reset link sent"); refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">{c?.name || "Contractor"}</DialogTitle></DialogHeader>
        {!c ? <p className="text-slate-400 py-8 text-center">Loading…</p> : (
          <div className="space-y-6">
            <div className="flex items-center gap-2">
              <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${STATUS_STYLES[c.status] || "bg-slate-100 text-slate-600"}`}>{c.status_display}</span>
              <span className="text-sm text-slate-500">{c.email}</span>
            </div>

            <section className="space-y-3">
              <h4 className="text-xs uppercase tracking-widest font-semibold text-slate-400">Step 1 · {noun} Agreement</h4>
              <div className="bg-slate-50 rounded-xl p-4 space-y-3">
                {c.agreement_filename ? (
                  <p className="flex items-center gap-2 text-sm text-navy font-medium"><FileText className="w-4 h-4 text-emerald-600" />{c.agreement_filename}</p>
                ) : defaultContract?.has_file ? (
                  <p className="flex items-center gap-2 text-sm text-slate-600"><FileText className="w-4 h-4 text-emerald-600" />Standard contract will be attached: <span className="font-medium text-navy">{defaultContract.filename}</span></p>
                ) : <p className="text-sm text-amber-600">No contract PDF. Upload one below, or set a standard contract in Settings → Email &amp; Communications.</p>}
                <input ref={fileRef} type="file" accept="application/pdf" className="hidden" onChange={(e) => upload(e.target.files[0])} data-testid="contractor-agreement-file" />
                <div className="flex flex-wrap gap-2">
                  <Button data-testid="upload-agreement-button" onClick={() => fileRef.current?.click()} variant="outline" size="sm" disabled={busy}><Upload className="w-4 h-4 mr-1.5" />{c.agreement_filename ? "Replace PDF" : "Attach PDF (optional)"}</Button>
                  <Button data-testid="send-agreement-button" onClick={sendAgreement} size="sm" className="bg-navy hover:bg-navy-light" disabled={busy || (!c.agreement_path && !defaultContract?.has_file)}><Send className="w-4 h-4 mr-1.5" />{c.status === "draft" ? "Send Agreement" : "Resend"}</Button>
                  {c.ack_token && c.status === "agreement_sent" && <Button data-testid="copy-ack-link-button" onClick={copyLink} variant="ghost" size="sm"><Copy className="w-4 h-4 mr-1.5" />Copy Link</Button>}
                </div>
                {c.status === "agreement_signed" && (
                  <p className="flex items-center gap-2 text-sm text-emerald-700 font-medium"><CheckCircle2 className="w-4 h-4" />Acknowledged by {c.ack_signed_name}</p>
                )}
              </div>
            </section>

            {c.user_id && (
              <section className="space-y-3" data-testid="contractor-login-section">
                <h4 className="text-xs uppercase tracking-widest font-semibold text-slate-400">Sign-in Details</h4>
                <div className="bg-slate-50 rounded-xl p-4 space-y-3">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-xs text-slate-400">Login email</p>
                      <p data-testid="login-email" className="font-medium text-navy truncate">{c.email}</p>
                    </div>
                    <Button data-testid="copy-login-email" onClick={copyEmail} variant="outline" size="sm"><Copy className="w-4 h-4 mr-1.5" />Copy</Button>
                  </div>
                  <p className="text-xs text-slate-400">Passwords are securely encrypted and can't be viewed. Resend the sign-in email, or send a secure link for the contractor to set a new password.</p>
                  <div className="flex flex-wrap gap-2">
                    <Button data-testid="resend-welcome-button" onClick={resendWelcome} disabled={busy} variant="outline" size="sm"><Send className="w-4 h-4 mr-1.5" />Resend Sign-in Email</Button>
                    <Button data-testid="reset-password-button" onClick={resetPassword} disabled={busy} variant="outline" size="sm"><KeyRound className="w-4 h-4 mr-1.5" />Reset Password</Button>
                  </div>
                </div>
              </section>
            )}

            {c.status !== "draft" && c.status !== "agreement_sent" && (
              <ReviewSection c={c} contractorId={id} onChange={() => { refresh(); onChange(); }} />
            )}

            {c.audit_log?.length > 0 && (
              <section className="space-y-2">
                <h4 className="text-xs uppercase tracking-widest font-semibold text-slate-400">Audit Log</h4>
                <div className="border border-slate-100 rounded-xl divide-y divide-slate-100 max-h-48 overflow-y-auto">
                  {[...c.audit_log].reverse().map((a, i) => (
                    <div key={i} className="px-4 py-2 text-sm flex justify-between gap-3">
                      <span className="text-slate-700">{a.action}{a.result ? ` · ${a.result}` : ""}{a.detail ? <span className="text-slate-400"> — {a.detail}</span> : ""}</span>
                      <span className="text-xs text-slate-400 shrink-0">{a.user}</span>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ReviewSection({ c, contractorId, onChange }) {
  const [note, setNote] = useState("");
  const [mode, setMode] = useState(null); // 'changes' | 'reject'
  const [busy, setBusy] = useState(false);
  const [editKey, setEditKey] = useState(null);
  const token = localStorage.getItem("ts_token");
  const docFile = (key) => `${API}/contractors/${contractorId}/documents/${key}/file?auth=${token}`;
  const ackFile = `${API}/contractors/${contractorId}/ack-pdf?auth=${token}`;
  const docs = c.documents_view || [];
  const canReview = ["under_review", "changes_requested", "profile_completed", "agreement_signed"].includes(c.status);

  const act = async (action, body) => {
    setBusy(true);
    try {
      await api.post(`/contractors/${contractorId}/${action}`, body || {});
      const msg = { approve: "Contractor approved & activated", "request-changes": "Changes requested", reject: "Contractor rejected" }[action];
      toast.success(msg); setMode(null); setNote(""); onChange();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const setDocNa = async (key, na) => {
    try {
      await api.put(`/contractors/${contractorId}/documents/${key}/na`, { na });
      toast.success(na ? "Marked Not Applicable — reminder emails stopped for this document" : "Marked as required again");
      onChange();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <section className="space-y-3" data-testid="contractor-review-section">
      <h4 className="text-xs uppercase tracking-widest font-semibold text-slate-400">Step 2 · Profile & Documents</h4>

      <div className="bg-slate-50 rounded-xl p-4 space-y-2 text-sm">
        <div className="flex items-center gap-2">
          {c.profile_complete ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <AlertTriangle className="w-4 h-4 text-amber-500" />}
          <span className="font-medium text-navy">Profile</span>
        </div>
        <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-slate-600 pl-6">
          <span>Mobile: {c.mobile || "—"}</span>
          <span>Email: {c.profile?.email || c.email || "—"}</span>
          <span>Address: {c.profile?.address || "—"}</span>
          <span>Suburb: {c.profile?.suburb || "—"}</span>
          <span>{c.profile?.state || ""} {c.profile?.postcode || ""}</span>
          <span className="col-span-2">Emergency: {c.emergency_contact?.name || "—"} {c.emergency_contact?.phone ? `(${c.emergency_contact.phone})` : ""}</span>
        </div>
      </div>

      <div className="border border-slate-100 rounded-xl divide-y divide-slate-100">
        {docs.map((d) => (
          <div key={d.key} data-testid={`review-doc-${d.key}`} className="px-4 py-2.5 text-sm">
            <div className="flex items-center justify-between gap-3">
              <span className="flex items-center gap-2">
                {d.na ? <Ban className="w-4 h-4 text-slate-400 shrink-0" /> : d.complete ? <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" /> : <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />}
                <span className={`text-slate-700 ${d.na ? "line-through text-slate-400" : ""}`}>{d.label}</span>
                {d.na && <span data-testid={`doc-na-badge-${d.key}`} className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-200 text-slate-600">N/A</span>}
                {!d.na && d.requires_expiry && d.expiry && <span className={`text-xs ${d.expiry_status === "expired" ? "text-red-600 font-semibold" : d.expiry_status === "expiring" ? "text-amber-600 font-semibold" : "text-slate-400"}`}>· exp {d.expiry}{d.expiry_status === "expired" ? " (expired)" : d.expiry_status === "expiring" ? ` (${d.expiry_days}d)` : ""}</span>}
                {!d.na && d.text && d.value && <span className="text-xs text-slate-400">· {d.value}</span>}
                {!d.na && d.bank && d.value?.account_name && <span className="text-xs text-slate-400">· {d.value.account_name} · BSB {d.value.bsb} · Acct {d.value.account_number}</span>}
              </span>
              <div className="flex items-center gap-3 shrink-0">
                {!d.na && d.filename && <a href={docFile(d.key)} target="_blank" rel="noreferrer" data-testid={`review-doc-view-${d.key}`} className="text-blue-700 text-xs font-medium inline-flex items-center gap-1"><FileText className="w-3.5 h-3.5" />View</a>}
                {!d.na && <button data-testid={`admin-doc-edit-${d.key}`} onClick={() => setEditKey(editKey === d.key ? null : d.key)} className="text-navy text-xs font-medium inline-flex items-center gap-1 hover:text-navy/70"><Upload className="w-3.5 h-3.5" />{d.complete ? "Replace" : "Add"}</button>}
                <button data-testid={`admin-doc-na-${d.key}`} onClick={() => setDocNa(d.key, !d.na)}
                  className={`text-xs font-medium inline-flex items-center gap-1 ${d.na ? "text-navy hover:text-navy/70" : "text-slate-400 hover:text-slate-600"}`}>
                  {d.na ? <><Upload className="w-3.5 h-3.5 rotate-180" />Mark required</> : <><Ban className="w-3.5 h-3.5" />N/A</>}
                </button>
              </div>
            </div>
            {!d.na && editKey === d.key && <AdminDocEditor d={d} contractorId={contractorId} onClose={() => setEditKey(null)} onSaved={() => { setEditKey(null); onChange(); }} />}
          </div>
        ))}
        {c.ack_pdf_available && (
          <div className="px-4 py-2.5 flex items-center justify-between gap-3 text-sm">
            <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500" /><span className="text-slate-700">Signed Contractor Agreement</span></span>
            <a href={ackFile} target="_blank" rel="noreferrer" className="text-blue-700 text-xs font-medium inline-flex items-center gap-1"><FileText className="w-3.5 h-3.5" />View</a>
          </div>
        )}
      </div>

      {c.review_note && (c.status === "changes_requested" || c.status === "rejected") && (
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 text-sm text-orange-800"><b>Note to contractor:</b> {c.review_note}</div>
      )}

      {c.status === "active" && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-sm text-emerald-800 flex items-center gap-2"><ShieldCheck className="w-4 h-4" />Activated — this contractor now has full access.</div>
      )}

      {canReview && (
        <div className="space-y-3 pt-1">
          {mode ? (
            <div className="space-y-2">
              <Label className="text-xs">{mode === "changes" ? "What changes are needed?" : "Reason for rejection (optional)"}</Label>
              <Textarea data-testid="review-note-input" value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder={mode === "changes" ? "Describe what the contractor needs to fix…" : "Optional note…"} />
              <div className="flex gap-2">
                <Button data-testid="review-note-confirm" onClick={() => act(mode === "changes" ? "request-changes" : "reject", { note })} disabled={busy || (mode === "changes" && !note.trim())} className={mode === "changes" ? "bg-orange-600 hover:bg-orange-700" : "bg-red-600 hover:bg-red-700"}>{mode === "changes" ? "Send Changes Request" : "Confirm Reject"}</Button>
                <Button variant="outline" onClick={() => { setMode(null); setNote(""); }}>Cancel</Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {c.profile_complete ? (
                <>
                  <Button data-testid="review-approve-button" onClick={() => act("approve")} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700"><ThumbsUp className="w-4 h-4 mr-1.5" />Approve &amp; Activate</Button>
                  {!c.documents_complete && (
                    <Button data-testid="review-activate-anyway-button" onClick={() => act("approve", { allow_outstanding: true })} disabled={busy} variant="outline" className="border-amber-400 text-amber-700 hover:bg-amber-50"><AlertTriangle className="w-4 h-4 mr-1.5" />Activate anyway (documents outstanding)</Button>
                  )}
                </>
              ) : (
                <p className="text-xs text-amber-600 w-full">Waiting on {c.worker_noun?.toLowerCase() || "worker"} to complete their profile before you can activate.</p>
              )}
              <Button data-testid="review-request-changes-button" onClick={() => setMode("changes")} disabled={busy} variant="outline" className="border-orange-300 text-orange-700 hover:bg-orange-50"><MessageSquareWarning className="w-4 h-4 mr-1.5" />Request Changes</Button>
              <Button data-testid="review-reject-button" onClick={() => setMode("reject")} disabled={busy} variant="outline" className="border-red-300 text-red-700 hover:bg-red-50"><XCircle className="w-4 h-4 mr-1.5" />Reject</Button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}


function AdminDocEditor({ d, contractorId, onClose, onSaved }) {
  const [busy, setBusy] = useState(false);
  const [file, setFile] = useState(null);
  const [expiry, setExpiry] = useState(d.expiry || "");
  const [textVal, setTextVal] = useState(d.text ? (d.value || "") : "");
  const [bank, setBank] = useState({
    account_name: d.value?.account_name || "",
    bsb: d.value?.bsb || "",
    account_number: d.value?.account_number || "",
  });

  const saveUpload = async () => {
    if (!file) { toast.error("Choose a file first."); return; }
    if (d.requires_expiry && !expiry) { toast.error(`Enter an expiry date for ${d.label}.`); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("expiry", expiry || "");
      await api.post(`/contractors/${contractorId}/documents/${d.key}`, fd);
      toast.success(`${d.label} uploaded`); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const saveText = async () => {
    if (d.bank && (!bank.account_name || !bank.bsb || !bank.account_number)) {
      toast.error("Account name, BSB and account number are all required."); return;
    }
    if (d.text && !textVal.trim()) { toast.error(`Enter a value for ${d.label}.`); return; }
    setBusy(true);
    try {
      const body = d.bank ? { value: bank } : { value: textVal.trim() };
      await api.put(`/contractors/${contractorId}/documents/${d.key}`, body);
      toast.success(`${d.label} saved`); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="w-full mt-2 bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-2" data-testid={`admin-doc-editor-${d.key}`}>
      {d.upload ? (
        <div className="space-y-2">
          <div>
            <Label className="text-xs">Upload {d.label} (on behalf of worker)</Label>
            <Input type="file" data-testid={`admin-doc-file-${d.key}`} onChange={(e) => setFile(e.target.files?.[0] || null)} className="mt-1 text-xs" />
          </div>
          {d.requires_expiry && (
            <div>
              <Label className="text-xs">Expiry date</Label>
              <Input type="date" data-testid={`admin-doc-expiry-${d.key}`} value={expiry} onChange={(e) => setExpiry(e.target.value)} className="mt-1 text-xs" />
            </div>
          )}
          <div className="flex gap-2">
            <Button size="sm" data-testid={`admin-doc-save-${d.key}`} onClick={saveUpload} disabled={busy} className="bg-navy hover:bg-navy/90 text-xs h-8">Save</Button>
            <Button size="sm" variant="outline" onClick={onClose} className="text-xs h-8">Cancel</Button>
          </div>
        </div>
      ) : d.bank ? (
        <div className="space-y-2">
          <Label className="text-xs">{d.label} (on behalf of worker)</Label>
          <Input placeholder="Account name" data-testid={`admin-doc-bank-name-${d.key}`} value={bank.account_name} onChange={(e) => setBank({ ...bank, account_name: e.target.value })} className="text-xs" />
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="BSB" data-testid={`admin-doc-bank-bsb-${d.key}`} value={bank.bsb} onChange={(e) => setBank({ ...bank, bsb: e.target.value })} className="text-xs" />
            <Input placeholder="Account number" data-testid={`admin-doc-bank-acct-${d.key}`} value={bank.account_number} onChange={(e) => setBank({ ...bank, account_number: e.target.value })} className="text-xs" />
          </div>
          <div className="flex gap-2">
            <Button size="sm" data-testid={`admin-doc-save-${d.key}`} onClick={saveText} disabled={busy} className="bg-navy hover:bg-navy/90 text-xs h-8">Save</Button>
            <Button size="sm" variant="outline" onClick={onClose} className="text-xs h-8">Cancel</Button>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <Label className="text-xs">{d.label} (on behalf of worker)</Label>
          <Input data-testid={`admin-doc-text-${d.key}`} value={textVal} onChange={(e) => setTextVal(e.target.value)} placeholder={`Enter ${d.label}`} className="text-xs" />
          <div className="flex gap-2">
            <Button size="sm" data-testid={`admin-doc-save-${d.key}`} onClick={saveText} disabled={busy} className="bg-navy hover:bg-navy/90 text-xs h-8">Save</Button>
            <Button size="sm" variant="outline" onClick={onClose} className="text-xs h-8">Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
}
